import { Routes } from '@angular/router';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout.component';
import { PublicLayoutComponent } from './layouts/public-layout/public-layout.component';
import { guardLinkGuard } from './guard/guard-link.guard';
import { adminGuard } from './guard/admin.guard';
import { employersGuard } from './guard/employers.guard';
import { affiliatesGuard } from './guard/affiliates.guard';
import { userHasAccessGuard } from './guard/user-has-access.guard';
import { userHasAccessToClientGuard } from './guard/user-has-access-to-client.guard';
import { affiliateLinkGuard } from './guard/affiliate-link.guard';
import { reportsGuard } from './guard/reports.guard';
import { sellerOrAdminGuard } from './guard/seller-or-admin.guard';
import { portalMatcher } from './pages/portal/portal.matcher';

export const routes: Routes = [
  { path: '', redirectTo: 'login', pathMatch: 'full' },

  // Rutas públicas (sin sidebar)
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      {
        path: 'login',
        loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
        title: 'Sign in · AlliantFed Portal',
      },
      {
        path: 'reset-password',
        loadComponent: () => import('./pages/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
      },
      {
        path: 'confirm-email/:email/:hashKey',
        loadComponent: () => import('./pages/manage-users/confirm-password/confirm-password.component').then(m => m.ConfirmPasswordComponent),
      },
      {
        path: 'landingPage/affiliateNewClient',
        loadComponent: () => import('./pages/landingPages/affiliate-create-client/affiliate-create-client.component').then(m => m.AffiliateCreateClientComponent),
        canActivate: [affiliateLinkGuard],
      },
      {
        path: 'landingPage/invalid-link',
        loadComponent: () => import('./pages/landingPages/invalid-link/invalid-link.component').then(m => m.InvalidLinkComponent),
        title: 'Alliant Federation',
      },
      {
        // Portal del cliente: /portal y /portal/<sección> con un mismo componente (no se recrea al cambiar de sección)
        matcher: portalMatcher,
        loadComponent: () => import('./pages/portal/client-portal.component').then(m => m.ClientPortalComponent),
        title: 'Portal del cliente · Alliant Federation',
      },
      {
        path: 'landingPage/contract/:fileName',
        loadComponent: () => import('./pages/landingPages/policy-viewer/policy-viewer.component').then(m => m.PolicyViewerComponent),
      },
      {
        path: 'terms',
        loadComponent: () => import('./pages/landingPages/terms-and-conditions/terms-and-conditions.component').then(m => m.TermsAndConditionsComponent),
      },
      {
        path: 'privacy-policy',
        loadComponent: () => import('./pages/landingPages/privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent),
      },
    ],
  },

  // Rutas protegidas (con sidebar)
  {
    path: '',
    component: AuthLayoutComponent,
    canActivate: [guardLinkGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () => import('./pages/new-dashboard/new-dashboard.component').then(m => m.NewDashboardComponent),
      },
      {
        path: 'pipeline',
        loadComponent: () => import('./pages/seller-pipeline/seller-pipeline.component').then(m => m.SellerPipelineComponent),
        canActivate: [affiliatesGuard],
      },
      {
        path: 'seller/payments',
        loadComponent: () => import('./pages/seller-payments/seller-payments.component').then(m => m.SellerPaymentsComponent),
        canActivate: [sellerOrAdminGuard],
      },
      {
        path: 'modify-client/:idClient',
        loadComponent: () => import('./pages/addClient/modify-client/modify-client.component').then(m => m.ModifyClientComponent),
        canActivate: [userHasAccessToClientGuard],
      },
      {
        path: 'select-benefits/:idClient/:mode/:idTransaction',
        loadComponent: () => import('./pages/addClient/page-select-benefits/page-select-benefits.component').then(m => m.PageSelectBenefitsComponent),
      },
      {
        path: 'add-dependents/:idTransaction',
        loadComponent: () => import('./pages/addClient/page-add-dependents/page-add-dependents.component').then(m => m.PageAddDependentsComponent),
        canActivate: [userHasAccessGuard],
      },
      {
        path: 'define-payment/:idTransaction',
        loadComponent: () => import('./pages/addClient/define-payment/define-payment.component').then(m => m.DefinePaymentComponent),
        canActivate: [affiliatesGuard, userHasAccessGuard],
      },
      {
        path: 'clientResume/:idTransaction',
        loadComponent: () => import('./pages/addClient/final-resume/final-resume.component').then(m => m.FinalResumeComponent),
        canActivate: [affiliatesGuard, userHasAccessGuard],
      },
      {
        path: 'newSelectBenefits/:idTransaction',
        loadComponent: () => import('./pages/addClient/new-select-benefits/new-select-benefits.component').then(m => m.NewSelectBenefitsComponent),
      },
      {
        path: 'manageEmployees',
        loadComponent: () => import('./pages/employer/manage-employees/manage-employees.component').then(m => m.ManageEmployeesComponent),
        canActivate: [employersGuard],
      },
      {
        path: 'employerPaymentList',
        loadComponent: () => import('./pages/employer/combine-payments/combine-payments.component').then(m => m.CombinePaymentsComponent),
        canActivate: [employersGuard],
      },
      {
        path: 'providers/clientList',
        loadComponent: () => import('./pages/providers/provider-clients/provider-clients.component').then(m => m.ProviderClientsComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/user',
        loadComponent: () => import('./pages/manage-users/users-admin/users-admin.component').then(m => m.UsersAdminComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/agencies',
        loadComponent: () => import('./pages/admin/agencies/page-agency/page-agency.component').then(m => m.PageAddAgencyComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/agency/add',
        loadComponent: () => import('./pages/admin/agencies/page-agency/page-agency.component').then(m => m.PageAddAgencyComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/affiliates',
        loadComponent: () => import('./pages/admin/affiliates/page-affiliate/page-affiliate.component').then(m => m.PageAffiliateComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/products',
        loadComponent: () => import('./pages/admin/products/products.component').then(m => m.ProductsComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/clients',
        loadComponent: () => import('./pages/admin/clients/client-list/client-list.component').then(m => m.ClientListComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/client-status',
        loadComponent: () => import('./pages/admin/client-status/client-status.component').then(m => m.ClientStatusComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/system-logs',
        loadComponent: () => import('./pages/admin/system-logs/system-logs.component').then(m => m.SystemLogsComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'testing/info',
        loadComponent: () => import('./pages/test-data/test-data.component').then(m => m.TestDataComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'reports/business-intelligence',
        loadComponent: () => import('./pages/reports/business-intelligence/business-intelligence.component').then(m => m.BusinessIntelligenceComponent),
        canActivate: [reportsGuard],
      },
      {
        path: 'admin/coverage-report',
        loadComponent: () => import('./pages/admin/coverage-report/coverage-report.component').then(m => m.CoverageReportComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/payments-collections',
        loadComponent: () => import('./pages/admin/payments-collections/payments-collections.component').then(m => m.PaymentsCollectionsComponent),
        canActivate: [adminGuard],
      },
      { path: 'admin/monthly-collections', redirectTo: 'admin/payments-collections' },
      { path: 'admin/payment-overview', redirectTo: 'admin/payments-collections' },
      { path: 'admin/tracear-payments', redirectTo: 'admin/cash-reconciliation' },
      {
        path: 'admin/cash-reconciliation',
        loadComponent: () => import('./pages/admin/cash-reconciliation/cash-reconciliation.component').then(m => m.CashReconciliationComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/seller-requests',
        loadComponent: () => import('./pages/admin-seller-requests/admin-seller-requests.component').then(m => m.AdminSellerRequestsComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/outbox-monitor',
        loadComponent: () => import('./pages/admin/outbox-monitor/outbox-monitor.component').then(m => m.OutboxMonitorComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/provider-tracking',
        loadComponent: () => import('./pages/admin/provider-tracking/provider-tracking.component').then(m => m.ProviderTrackingComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'admin/provider-financials',
        loadComponent: () => import('./pages/admin/provider-financials/provider-financials.component').then(m => m.ProviderFinancialsComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'seller/site-leads',
        loadComponent: () => import('./pages/seller-site-leads/seller-site-leads.component').then(m => m.SellerSiteLeadsComponent),
        canActivate: [sellerOrAdminGuard], // admins: solo lectura
      },
      {
        path: 'admin/site-leads',
        loadComponent: () => import('./pages/admin/site-leads/admin-site-leads.component').then(m => m.AdminSiteLeadsComponent),
        canActivate: [adminGuard],
      },
      {
        path: 'seller/messages',
        loadComponent: () => import('./pages/seller-messages/seller-messages.component').then(m => m.SellerMessagesComponent),
        canActivate: [sellerOrAdminGuard],
      },
    ],
  },

  { path: '**', redirectTo: 'login' },
];
